import * as React from 'react';
import { getLookupCached, LookupItem } from '../api/lookup';
import type { LookupFieldMapping } from '../api/search';

interface LookupFieldInputProps {
  lookupName: string;
  keyColumn: string;
  displayColumn: string;
  fieldName: string;
  mappings?: LookupFieldMapping[];
  value: string;
  onSelect: (values: Record<string, any>) => void;
  className?: string;
}

export function LookupFieldInput({
  lookupName, keyColumn, displayColumn, fieldName, mappings, value, onSelect, className
}: LookupFieldInputProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [items, setItems] = React.useState<LookupItem[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [highlightIdx, setHighlightIdx] = React.useState(0);
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // Eager-load on mount so display value resolves without user interaction
  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getLookupCached(lookupName)
      .then(data => { if (!cancelled) { setItems(data); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lookupName]);

  // Display value: the displayColumn value for the current key
  const displayValue = React.useMemo(() => {
    if (!value) return '';
    const match = items.find(item => String(item[keyColumn] ?? '') === String(value));
    return match ? `${String(match[keyColumn] ?? '')} - ${String(match[displayColumn] ?? '')}` : value;
  }, [value, items, keyColumn, displayColumn]);

  const filtered = React.useMemo(() => {
    if (!open) return [];
    const q = search.toLowerCase();
    if (!q) return items;
    return items.filter(item => String(item[displayColumn] ?? '').toLowerCase().includes(q));
  }, [items, search, displayColumn, open]);

  React.useEffect(() => { setHighlightIdx(0); }, [filtered]);

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function handleFocus() {
    setOpen(true);
    setSearch('');
  }

  function handleSelect(item: LookupItem) {
    const values: Record<string, any> = { [fieldName]: String(item[keyColumn] ?? '') };
    if (mappings) {
      for (const m of mappings) values[m.targetFieldName] = String(item[m.lookupColumn] ?? '');
    }
    onSelect(values);
    setOpen(false);
    setSearch('');
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!open) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlightIdx]) handleSelect(filtered[highlightIdx]); }
    else if (e.key === 'Escape') setOpen(false);
  }

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        className={className}
        value={open ? search : displayValue}
        onChange={e => { setSearch(e.target.value); if (!open) setOpen(true); }}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
        placeholder={loading ? 'Laden...' : 'Zoeken...'}
        autoComplete="off"
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: '#fff', border: '1px solid #cfd8dc', borderRadius: 4,
          maxHeight: 220, overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.12)'
        }}>
          {loading && <div style={{ padding: '8px 12px', color: '#888', fontSize: 13 }}>Laden...</div>}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: '8px 12px', color: '#888', fontSize: 13 }}>Geen resultaten</div>
          )}
          {filtered.map((item, i) => (
            <div
              key={i}
              onMouseDown={e => { e.preventDefault(); handleSelect(item); }}
              onMouseEnter={() => setHighlightIdx(i)}
              style={{
                padding: '6px 12px', cursor: 'pointer', fontSize: 14,
                borderBottom: '1px solid #f0f4f7',
                background: i === highlightIdx ? '#eaf1f7' : ''
              }}
            >
              {`${String(item[keyColumn] ?? '')} - ${String(item[displayColumn] ?? '')}`}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
